import Cookies from 'js-cookie';

/*
 * Decides whether, and when, a visitor gets a PostHog person profile (SPE-4047).
 *
 * Since SPE-3898 the SDK is bootstrapped with the upref uid and never
 * identify()s, so every visitor is anonymous and $set_once ($initial_gclid,
 * $initial_utm_*, …) never reaches a person; the Google/Meta conversion
 * destinations then have nothing to fall back on. posthog.createPersonProfile()
 * restores that without touching identity, but person-processed events are the
 * pricier tier and, once a profile exists, PostHog copies the campaign params
 * (gclid, fbclid, utm_*) and the URL it finds on every event onto the person.
 *
 * So a profile is requested only when there is something to attribute (an ad
 * click id or UTM on the current URL or on the first-touch landing URL the SDK
 * persisted, or a first-party ad cookie left by an earlier attributed visit)
 * AND, on labels with a CMP, only once Cookiebot reports marketing consent.
 * Nothing here is sent anywhere; the SDK's own $set / $unset events do the
 * work.
 *
 * Change-together pair with api/services/tracking/migrationContextService.js,
 * which resolves the same consent states from Cookiebot's `CookieConsent`
 * cookie for the X-Tracking-Context header. See
 * docs/prd/2026-09-29-ad-click-attribution-tracking-context.md.
 */
/*
 * Every ad click id posthog-js 1.396.4 copies onto a person: its
 * PERSONAL_DATA_CAMPAIGN_PARAMS plus gad_source (utils/event-utils.js
 * CAMPAIGN_PARAMS; the rest are UTM and Mailchimp's mc_cid). Once a profile
 * exists the SDK sends them all, whichever brought the visitor in: plain from
 * the page's campaign params (li_fat_id from its cookie too), $initial_ from
 * the persisted first touch. The one source for both the profile signal and
 * the withdrawal; the unit test pins it against the installed SDK.
 */
const PERSON_CLICK_ID_PARAMS = Object.freeze([
	      'gclid', 'gclsrc', 'dclid', 'gbraid', 'wbraid', 'gad_source', 'fbclid', 'msclkid', 'twclid', 'li_fat_id',
	      'igshid', 'ttclid', 'rdt_cid', 'epik', 'qclid', 'sccid', 'irclid', '_kx'
      ]),

      /*
       * The click ids that count as an ad click for the profile signal, taken
       * from the list above so the withdrawal always covers them. Widening the
       * signal changes who gets a profile, so the rest (Instagram's share id,
       * Klaviyo's email id, …) stay out of the decision; a profile that exists
       * still carries them, so the withdrawal removes the whole list.
       */
      AD_CLICK_ID_PARAMS = Object.freeze(PERSON_CLICK_ID_PARAMS.filter((key) => [
	      'gclid', 'gbraid', 'wbraid', 'dclid', 'gad_source', 'fbclid', 'msclkid', 'ttclid', 'twclid', 'li_fat_id'
      ].includes(key))),
      UTM_PARAM_PREFIX = 'utm_',

      // Written by Google's conversion linker and Meta's pixel. Presence is
      // enough here: the server parses the values and bounds the click age.
      AD_CLICK_COOKIES = Object.freeze(['_gcl_aw', '_gcl_gb', '_gcl_ag', '_fbc']),

      /*
       * Person properties the ad destinations can read as a fallback; removed
       * when marketing consent is withdrawn: every click id in both forms,
       * derived from PERSON_CLICK_ID_PARAMS. $initial_current_url is the full
       * landing URL, click id included, and $current_url / $referrer are lifted
       * from every person-processed event, so they go too. UTM, mc_cid and the
       * other $initial_* values stay: no ad destination reads them and PostHog
       * itself runs under the site's statistics posture, not the marketing one.
       */
      ATTRIBUTION_PERSON_PROPERTIES = Object.freeze([
	      ...PERSON_CLICK_ID_PARAMS,
	      ...PERSON_CLICK_ID_PARAMS.map((key) => `$initial_${key}`),
	      '$current_url', '$initial_current_url', '$referrer', '$initial_referrer'
      ]),

      MARKETING_CONSENT_GRANTED      = 'granted',
      MARKETING_CONSENT_DENIED       = 'denied',
      MARKETING_CONSENT_UNKNOWN      = 'unknown',
      MARKETING_CONSENT_NOT_REQUIRED = 'not_required',

      /**
       * @param   {unknown} url an absolute URL
       * @returns {boolean} true when its query carries a non-empty ad click id or a UTM
       */
      hasAdParams = (url) => {
	      if (typeof url !== 'string') {
		      return false;
	      }

	      let params;

	      try {
		      params = new URL(url).searchParams;
	      } catch {
		      return false;
	      }

	      for (const [key, value] of params) {
		      if (value && (AD_CLICK_ID_PARAMS.includes(key) || key.startsWith(UTM_PARAM_PREFIX))) {
			      return true;
		      }
	      }

	      return false;
      },

      /**
       * Whether a profile would have something to attribute. The first touch
       * a profile carries ($initial_*) comes from posthog-js's persisted
       * `$initial_person_info` (u = the landing URL of the first captured page),
       * so its URL counts wherever the decision arrives, e.g. on a later page
       * of the booking SPA. The current URL covers the landing page itself:
       * the SDK persists the record with its first capture, after `loaded`.
       * The referrer is not a signal (an organic search visit is no ad click).
       * @param   {{u?: string}} [initialPersonInfo] posthog-js's `$initial_person_info`, when persisted
       * @returns {boolean} true when either URL carries an ad click id or a UTM, or an ad click cookie exists
       */
      hasAdAttributionSignal = (initialPersonInfo) => (
	      hasAdParams(window.location.href) ||
	      hasAdParams(initialPersonInfo?.u) ||
	      AD_CLICK_COOKIES.some((cookieName) => Boolean(Cookies.get(cookieName)))
      ),

      /**
       * Whether Cookiebot holds a decision for this visitor (uc.js 2.138.0,
       * verified 2026-09-30). Loading or recording a decision sets
       * `hasResponse` together with `consented` or `declined`; show() (the
       * storefront's #CookiebotShow footer link, e.g. on esp) and renew()
       * (Cookiebot's API for a "change consent" link) reopen the dialog for a
       * review, clear `hasResponse` alone and leave the stored decision (and
       * the CookieConsent cookie) in force until the visitor submits. No decision on record (no cookie, a "-2" cookie,
       * a consent reset by a new version, withdraw()) clears all three.
       * @param   {{hasResponse?: boolean, consented?: boolean, declined?: boolean}} [cookiebot] window.Cookiebot
       * @returns {boolean}
       */
      hasDecisionOnRecord = (cookiebot) => Boolean(cookiebot?.hasResponse || cookiebot?.consented || cookiebot?.declined),

      /**
       * The same four states the server resolves from the CookieConsent cookie.
       * A label without its own Cookiebot id runs every tag without a dialog
       * (business decision), so consent is not required there: the page
       * renders `frontendConfig.get('cookiebot') ?? null`, which is null for
       * scp/scp_* and com (`~`) and false for every label inheriting
       * shared.yml's default (e.g. set). A missing key is defensive only (the
       * page always renders it) and fails closed. On a CMP label Cookiebot's
       * read-only API decides: a decision is on record once the visitor
       * answered, a stored decision was loaded, or the visitor is outside a
       * regulated region, and stays on record while the visitor reviews it
       * (hasDecisionOnRecord); `consent.marketing` holds the decision. The
       * method (explicit/implied) is the CMP's own configuration and is not
       * re-judged.
       * @returns {'granted'|'denied'|'unknown'|'not_required'}
       */
      getMarketingConsentState = () => {
	      const cmp = window.globalParams?.cookiebot;

	      if (cmp === false || cmp === null) {
		      return MARKETING_CONSENT_NOT_REQUIRED;
	      }

	      if (cmp === undefined || !hasDecisionOnRecord(window.Cookiebot)) {
		      return MARKETING_CONSENT_UNKNOWN;
	      }

	      return window.Cookiebot.consent?.marketing === true ? MARKETING_CONSENT_GRANTED : MARKETING_CONSENT_DENIED;
      },

      /**
       * @param   {string} state one of the MARKETING_CONSENT_* values
       * @returns {boolean}
       */
      allowsPersonProfile = (state) => state === MARKETING_CONSENT_GRANTED || state === MARKETING_CONSENT_NOT_REQUIRED,

      /**
       * Whether an existing profile must stop: no person processing without
       * marketing consent where a CMP decides. "unknown" counts once Cookiebot
       * has loaded: uc.js assigns window.Cookiebot and reads the stored
       * CookieConsent in one synchronous init, so "unknown" then means no
       * decision on record (a lapsed consent, or one reset by a new consent
       * version); a stored decision under review is not "unknown"
       * (hasDecisionOnRecord). Before uc.js has run (it loads async) "unknown"
       * is only "not loaded yet"; the Cookiebot event that follows re-runs
       * the sync.
       * @param   {string} state one of the MARKETING_CONSENT_* values
       * @returns {boolean}
       */
      withdrawsPersonProfile = (state) => (
	      state === MARKETING_CONSENT_DENIED ||
	      (state === MARKETING_CONSENT_UNKNOWN && window.Cookiebot !== undefined)
      );

export {
	AD_CLICK_ID_PARAMS,
	ATTRIBUTION_PERSON_PROPERTIES,
	PERSON_CLICK_ID_PARAMS,
	MARKETING_CONSENT_DENIED,
	MARKETING_CONSENT_GRANTED,
	MARKETING_CONSENT_NOT_REQUIRED,
	MARKETING_CONSENT_UNKNOWN,
	allowsPersonProfile,
	getMarketingConsentState,
	hasAdAttributionSignal,
	withdrawsPersonProfile
};